import {
    Autocomplete,
    AutocompleteOption,
    CircularProgress,
    ListItemContent,
    Typography,
    VariantProp,
} from '@healthinal/ui';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

type FetchResult<T> = {
    items: T[];
    hasMore: boolean;
};

type FetchOptionsFn<T> = (params: {
    search: string;
    page: number;
    pageSize: number;
    signal?: AbortSignal;
}) => Promise<FetchResult<T>>;

type SearchTextFn<T> = (option: T) => string;

interface GenericAutocompleteProps<T, Multiple extends boolean = false> {
    value: Multiple extends true ? T[] : T | null;
    onChange: (value: Multiple extends true ? T[] : T | null) => void;

    options?: T[];
    fetchOptions?: FetchOptionsFn<T>;

    getOptionLabel: (option: T) => string;
    isOptionEqualToValue?: (option: T, value: T) => boolean;
    getOptionKey?: (option: T) => string | number;
    renderOptionContent?: (option: T) => ReactNode;
    getSearchText?: SearchTextFn<T>;

    multiple?: Multiple;
    variant?: VariantProp;
    disabled?: boolean;
    placeholder?: string;
    noOptionsText?: string;

    enableSearch?: boolean;
    enableLazyLoad?: boolean;

    pageSize?: number;
    debounceMs?: number;
}

function useDebouncedValue<T>(value: T, delay = 300) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => window.clearTimeout(timeout);
    }, [value, delay]);

    return debouncedValue;
}

export function GenericAutocomplete<T, Multiple extends boolean = false>({
    value,
    onChange,
    options = [],
    fetchOptions,
    getOptionLabel,
    isOptionEqualToValue,
    getOptionKey,
    renderOptionContent,
    getSearchText,
    multiple,
    variant,
    disabled,
    placeholder,
    noOptionsText = 'Keine Optionen',
    enableSearch = false,
    enableLazyLoad = false,
    pageSize = 20,
    debounceMs = 300,
}: GenericAutocompleteProps<T, Multiple>) {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [remoteOptions, setRemoteOptions] = useState<T[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);

    const pageRef = useRef(0);
    const abortControllerRef = useRef<AbortController | null>(null);

    const debouncedInputValue = useDebouncedValue(inputValue, debounceMs);
    const effectiveSearch = enableSearch ? debouncedInputValue.trim() : '';

    const resolveSearchText = useCallback(
        (option: T) => {
            if (getSearchText) return getSearchText(option);
            return getOptionLabel(option);
        },
        [getOptionLabel, getSearchText],
    );

    const loadPage = useCallback(
        async (page: number, replace: boolean) => {
            if (!fetchOptions) return;

            abortControllerRef.current?.abort();
            const controller = new AbortController();
            abortControllerRef.current = controller;

            setIsLoading(true);

            try {
                const response = await fetchOptions({
                    search: effectiveSearch,
                    page,
                    pageSize,
                    signal: controller.signal,
                });

                setHasMore(response.hasMore);
                pageRef.current = page;

                setRemoteOptions((prev) =>
                    replace ? response.items : [...prev, ...response.items],
                );
            } finally {
                setIsLoading(false);
            }
        },
        [fetchOptions, effectiveSearch, pageSize],
    );

    useEffect(() => {
        if (!fetchOptions || !open) return;
        void loadPage(0, true);

        return () => {
            abortControllerRef.current?.abort();
        };
    }, [fetchOptions, open, effectiveSearch, loadPage]);

    const localOptions = useMemo(() => {
        if (fetchOptions) return [];

        if (!enableSearch || !effectiveSearch) {
            return options;
        }

        const normalizedSearch = effectiveSearch.toLowerCase();

        return options.filter((option) =>
            resolveSearchText(option).toLowerCase().includes(normalizedSearch),
        );
    }, [fetchOptions, options, enableSearch, effectiveSearch, resolveSearchText]);

    const resolvedOptions = fetchOptions ? remoteOptions : localOptions;

    const handleLoadMore = async (event: React.UIEvent<HTMLUListElement>) => {
        if (!fetchOptions || !enableLazyLoad || isLoading || !hasMore) return;

        const listbox = event.currentTarget;
        const reachedBottom =
            listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 24;

        if (!reachedBottom) return;

        await loadPage(pageRef.current + 1, false);
    };

    return (
        <Autocomplete
            multiple={multiple}
            value={value}
            options={resolvedOptions}
            variant={variant}
            disabled={disabled}
            placeholder={placeholder}
            loading={isLoading}
            noOptionsText={noOptionsText}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            filterOptions={(x) => x}
            onChange={(_, newValue) =>
                onChange(newValue as Multiple extends true ? T[] : T | null)
            }
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            {...(enableSearch
                ? {
                    inputValue,
                    onInputChange: (_: unknown, newValue: string) => {
                        setInputValue(newValue);
                    },
                }
                : {})}
            slotProps={{
                listbox: {
                    onScroll: handleLoadMore,
                },
                input: {
                    readOnly: !enableSearch,
                },
            }}
            renderOption={(props, option) => (
                <AutocompleteOption
                    {...props}
                    key={String(getOptionKey ? getOptionKey(option) : getOptionLabel(option))}
                >
                    {renderOptionContent ? (
                        renderOptionContent(option)
                    ) : (
                        <ListItemContent>
                            <Typography level="title-sm">{getOptionLabel(option)}</Typography>
                        </ListItemContent>
                    )}
                </AutocompleteOption>
            )}
            endDecorator={isLoading ? <CircularProgress size="sm" /> : null}
        />
    );
}